import React, { useContext, useState, useEffect, useMemo } from 'react';
import './App.css';
import SubmitPostComponent from './SubmitPostComponent/SubmitPostComponent';
import Feed from './FeedComponent/FeedComponent';
import DeathRow from './components/DeathRow';
import LastCall from './components/LastCall';
import MortalityWeather from './components/MortalityWeather';
import DailyCheckin from './components/DailyCheckin';
import AnniversaryBanner from './components/AnniversaryBanner';
import LowWalletBanner from './components/LowWalletBanner';
import GuidedFirstPost from './components/GuidedFirstPost';
import PageMeta, { SITE_JSONLD } from './components/PageMeta';
import { AuthContext, MyContext } from './context/AuthContext';
import { CacheContext, CacheContextType } from './context/CacheContext';
import { stringToArray, getSchedule } from 'cron-converter';
import './fonts.css';
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { DateTime } from 'luxon';

const TAGLINES = [
  'The social media site that deletes your data; ON PURPOSE',
  "We're all living on borrowed time",
  'Like Twitter, but it actually deletes things',
  'Like that shitty Justin Timberlake movie',
  'Every post is on death row',
  'Fame is fleeting. So are your posts.',
];

function HomePage() {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const { allFeeds } = useContext<CacheContextType>(CacheContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const trendingFeeds = useMemo(() => {
    const now = DateTime.now();
    return allFeeds()
      .filter((f) => !f.is_expired && (f.expire_at ? DateTime.fromISO(f.expire_at) > now : true))
      .sort((a, b) => {
        const aExp = a.expire_at ? DateTime.fromISO(a.expire_at).toSeconds() : 0;
        const bExp = b.expire_at ? DateTime.fromISO(b.expire_at).toSeconds() : 0;
        return bExp - aExp;
      })
      .slice(0, 8);
  }, [allFeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  const wipeDate = useMemo(
    () => getSchedule(stringToArray('0 0 1 * *'), new Date(), 'America/Los_Angeles').next().toJSDate(),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [tagline] = useState(() => TAGLINES[Math.floor(Math.random() * TAGLINES.length)]);
  const [wipeSeconds, setWipeSeconds] = useState(
    Math.max(0, Math.floor((wipeDate.getTime() - Date.now()) / 1000)),
  );

  useEffect(() => {
    const id = setInterval(() => {
      setWipeSeconds(Math.max(0, Math.floor((wipeDate.getTime() - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [wipeDate]);

  const wipeDays = Math.floor(wipeSeconds / 86400);
  const wipeHours = Math.floor((wipeSeconds % 86400) / 3600);
  const wipeMins = Math.floor((wipeSeconds % 3600) / 60);
  const wipeSecs = wipeSeconds % 60;

  return (
    // No Container/maxWidth wrapper — the AppShell grid already caps the
    // main column at 720/760px and centers it between the rail and the
    // aside. Wrapping in another Container was double-centering and
    // produced the mobile overflow bugs we chased for days.
    <Box
      sx={{
        py: { xs: 2, sm: 3 },
        px: { xs: 1.5, sm: 2 },
        width: '100%',
        minWidth: 0,
      }}
    >
      <PageMeta path="/" jsonLd={SITE_JSONLD} />
      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 0,
          border: '2px solid currentColor',
          // Hero respects the theme. Dark = Nokia screen-off black with a
          // faint amber radial. Light = pale Nokia LCD with a hard pixel
          // border (no gradient — flat retro chrome). Both modes keep
          // their text contrast WCAG-AA on the chosen background.
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0b0f0b 0%, #1f241f 100%)'
            : theme.palette.background.default,
          color: theme.palette.mode === 'dark' ? '#f4f0e8' : '#0b0f0b',
          position: 'relative',
          overflow: 'hidden',
          maxWidth: '100%',
          '&::before': theme.palette.mode === 'dark'
            ? {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(ellipse at 70% 50%, rgba(232,179,42,0.10) 0%, transparent 70%)',
              }
            : undefined,
        }}
      >
        <Box sx={{ position: 'relative', textAlign: 'center' }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' },
              letterSpacing: { xs: 1.5, sm: 4, md: 6 },
              mb: 0.5,
              lineHeight: 1.1,
              wordBreak: 'break-word',
            }}
          >
            WIPEHOUSE
          </Typography>

          <Typography
            sx={{
              fontFamily: '"VT323", monospace',
              fontSize: { xs: '0.95rem', sm: '1.3rem' },
              opacity: 0.7,
              letterSpacing: 1,
              mb: 2,
              px: 1,
            }}
          >
            {tagline}
          </Typography>

          {/* Live countdown — wraps on ultra-narrow viewports so it
              never forces horizontal scroll. `inline-flex` with a max
              width keeps the pill centered under the tagline. */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 0.5, sm: 1 },
              // Pixel-bordered countdown pill — uses currentColor so it
              // tracks whichever foreground the hero picked.
              background: 'transparent',
              border: '2px solid currentColor',
              borderRadius: 0,
              px: { xs: 1.25, sm: 2 },
              py: 0.75,
              fontFamily: '"VT323", monospace',
              fontSize: 'clamp(0.9rem, 1.2vw + 0.6rem, 1.3rem)',
              letterSpacing: { xs: 0.5, sm: 1 },
              color: 'inherit',
              maxWidth: '100%',
              flexWrap: 'wrap',
            }}
          >
            <AccessTimeIcon sx={{ fontSize: '1rem', opacity: 0.8 }} />
            <Box component="span">WIPE IN</Box>
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {wipeDays}d {String(wipeHours).padStart(2,'0')}h {String(wipeMins).padStart(2,'0')}m {String(wipeSecs).padStart(2,'0')}s
            </Box>
          </Box>

          {/* CTA for logged-out users */}
          {!userRecord && (
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button component={Link} to="/signup" variant="contained" size="medium">
                Get Started
              </Button>
              <Button
                component={Link}
                to="/about"
                variant="outlined"
                size="medium"
                sx={{ color: 'inherit', borderColor: 'currentColor' }}
              >
                Learn More
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Trending feeds bar */}
      {trendingFeeds.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Trending:
          </Typography>
          {trendingFeeds.map((feed) => (
            <Chip
              key={feed.id}
              label={`#${feed.feed_tag}`}
              size="small"
              component={Link}
              to={`/feeds/${feed.feed_tag}`}
              clickable
              sx={{ fontSize: '0.72rem', height: 22, '& .MuiChip-label': { px: 0.75 } }}
            />
          ))}
        </Box>
      )}

      {/* Mortality weather — ambient vitals of the time economy */}
      <MortalityWeather />

      {/* Anniversary bonus — only on join_date MM-DD, once per year */}
      {userRecord && <AnniversaryBanner />}

      {/* Daily check-in bonus — free wallet refill if eligible today */}
      {userRecord && <DailyCheckin />}

      {/* Low wallet warning — only when under 1h */}
      {userRecord && <LowWalletBanner />}

      {/* Guided first post — only for brand-new signups in first 10min */}
      {userRecord && <GuidedFirstPost />}

      {/* Composer */}
      {userRecord && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ p: isMobile ? 1.5 : 2, mb: 3, borderRadius: 2 }}
        >
          <SubmitPostComponent objectType="posts" />
        </Paper>
      )}

      {/* Last Call — midnight UTC ritual (only visible 23:00–01:00 UTC) */}
      <LastCall />

      {/* Death Row — posts about to expire */}
      <DeathRow />

      {/* Feed */}
      <Box>
        {userRecord ? (
          <Feed showMenu={true} />
        ) : (
          <Feed />
        )}
      </Box>
    </Box>
  );
}

export default HomePage;
